import { reportError } from '@ms/yammer-telemetry';
import { create } from 'zustand';

import { GlobalCopilotInstance, useGlobalCopilotInstanceStore } from './globalCopilotInstanceStore';
import type { GlobalCopilotContext } from './types';

export interface GlobalCopilotState {
  /*
   * Whether the Global Copilot is open.
   */
  readonly isOpen: boolean;

  /*
   * Opens the Global Copilot. Does nothing if the Global Copilot is already open.
   * @returns A promise that resolves to the current Global Copilot instance once it is loaded and ready. Rejects if the instance is null.
   */
  open(): Promise<Exclude<GlobalCopilotInstance, null>>;

  /*
   * Closes the Global Copilot.
   */
  close(): void;

  /*
   * The session operation context data used in the Global Copilot.
   */
  readonly sessionOperationContextData: GlobalCopilotContext | null;

  /*
   * Replaces the session operation context data used in the Global Copilot.
   */
  replaceSessionOperationContextData(sessionOperationContextData: GlobalCopilotContext): void;

  /*
   * Clears the session operation context data used in the Global Copilot.
   */
  clearSessionOperationContextData(): void;

  /*
   * Whether to use the session operation context data in the Global Copilot.
   */
  readonly isUsingContext: boolean;
}

export const useGlobalCopilotStore = create<GlobalCopilotState>((set) => {
  const open = () => ({ isOpen: true });
  const close = () => {
    useGlobalCopilotInstanceStore.getState().resetInstancePromise();

    return { isOpen: false };
  };

  return {
    isOpen: false,
    open: async () => {
      set(open);

      const instance = await useGlobalCopilotInstanceStore.getState().instancePromise;

      if (instance == null) {
        reportError({
          error: new Error('GlobalCopilotInitFailed'),
          eventProperties: {
            errorCode: 'GlobalCopilotInitFailed',
          },
        });

        return Promise.reject(new Error('GlobalCopilotInitFailed'));
      }

      return instance;
    },
    close: () => set(close),
    sessionOperationContextData: null,
    clearSessionOperationContextData: () => set({ sessionOperationContextData: null, isUsingContext: false }),
    replaceSessionOperationContextData: (sessionOperationContextData) =>
      set({
        sessionOperationContextData,
        isUsingContext: true,
      }),
    isUsingContext: false,
  };
});
